import { memo, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import styles from './CreateEstimateDialog.module.scss';
import classNames from 'classnames/bind';
import PaymentCalculation from '~/components/estimate/payment-calculation/PaymentCalculation';
import PriceListItem from '~/components/estimate/price-list-item/PriceListItem';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import { CloseIcon } from '~/assets/icons';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { cast, handleApiError, notify } from '~/utils/helper';
import { IBodyCreateEstimate, IResponseGetEstimate } from '~/utils/interface/estimate';
import ClientInformation from '~/components/estimate/client-information/ClientInformation';
import FileItem from '~/components/estimate/file-item/FileItem';
import { handleCreateEstimate, handleUpdateEstimate } from '~/thunks/estimate/estimateThunk';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { estimateActions } from '~/thunks/estimate/estimateSlice';
import TermAndCondition from '~/components/estimate/term-and-condition/TermAndCondition';
import { IJob } from '~/utils/interface/job';
import SaveAsTemplateDialog from '~/components/estimate-template/dialog/SaveAsTemplateDialog';
import { handleCreateEstimateTemplate } from '~/thunks/estimate-template/estimateTemplateThunk';
import { FILE_ITEM_FOR_ESTIMATE } from '~/utils/constants/jobs';
import OpeningStatement from '~/components/estimate/opening-statement/OpeningStatement';
import Esign from '~/components/estimate/esign/Esign';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_UNITS } from '~/utils/constants/master';

const cx = classNames.bind(styles);

type EstimateListProps = {
  jobId: string;
  data: IJob | undefined;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  open: boolean;
  mode?: string;
  editData?: IResponseGetEstimate | undefined;
  updateData: () => void;
};

const initialValues: IBodyCreateEstimate = {
  name: '',
  tax: 0,
  subtotal: 0,
  total: 0,
  estimateItems: [],
  isGeneratePDFPricing: true,
};

function CreateEstimateDialog(props: EstimateListProps) {
  const { mode, editData } = props;
  const dispatch = useAppDispatch();
  const {
    profile: { tax },
  } = useAppSelector((state) => state.auth);
  const { t } = useTranslation(['admin/job', 'admin/invoice']);

  const [disableSubmit, setDisableSubmit] = useState(false);
  const [openSaveAsTemplateDialog, setOpenSaveAsTemplateDialog] = useState<boolean>(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState<boolean>(false);
  const [companyTax, setCompanyTax] = useState<number>(0);
  const [nameSaveAsTemplate, setNameSaveAsTemplate] = useState<string>();

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      setDisableSubmit(true);
      values.estimateItems = values.estimateItems?.filter((item) => item.priceItemId).map(({ name, ...rest }) => rest);
      if (saveAsTemplate) {
        const { isGeneratePDFPricing, ...submitValues } = values;
        try {
          await dispatch(
            handleCreateEstimateTemplate({
              data: { ...submitValues, name: nameSaveAsTemplate },
            })
          ).unwrap();
          handleOpenSaveAsTemplateDialog(false);
          setSaveAsTemplate(false);
          setDisableSubmit(false);
          setOpenSaveAsTemplateDialog(false);
          notify('success', t('createSuccess', { ns: 'admin/common' }));
        } catch (e) {
          setSaveAsTemplate(false);
          setDisableSubmit(false);
          handleApiError(e, setFieldError);
        }
      } else {
        if (mode === DIALOG_MODE_CREATE) {
          try {
            await dispatch(handleCreateEstimate({ jobId: props.jobId, data: values })).unwrap();

            handleSetOpen(false);
            notify('success', t('createSuccess', { ns: 'admin/common' }));
            dispatch(estimateActions.changeNeedRefreshData(true));
            setDisableSubmit(false);
            if (props.updateData) props.updateData();
          } catch (e: unknown) {
            setDisableSubmit(false);
            handleApiError(e, setFieldError);
          }
          return;
        }
        if (mode === DIALOG_MODE_EDIT) {
          try {
            editData?.id &&
              (await dispatch(handleUpdateEstimate({ estimateId: editData.id.toString(), data: values })).unwrap());
            // (await dispatch(handleGeneratePDF( editData.id.toString())).unwrap());

            notify('success', t('editSuccess', { ns: 'admin/common' }));
            dispatch(estimateActions.changeNeedRefreshData(true));
            handleSetOpen(false);
            setDisableSubmit(false);
            if (props.updateData) props.updateData();
          } catch (e: unknown) {
            setDisableSubmit(false);
            handleApiError(e, setFieldError);
          }
          return;
        }
      }
    },
  });

  useEffect(() => {
    if (form.errors.name) setOpenSaveAsTemplateDialog(true);
    else setOpenSaveAsTemplateDialog(false);
  }, [form.errors]);

  useEffect(() => {
    if (tax) setCompanyTax(tax);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_UNITS],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (props.mode === DIALOG_MODE_CREATE && props.open) {
      form.setFieldValue('name', props?.data?.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.mode]);

  useEffect(() => {
    if (props.mode === DIALOG_MODE_EDIT && props.open) {
      form.setFieldValue('name', props?.editData?.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.mode, props?.editData]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (mode === DIALOG_MODE_CREATE) {
      form.resetForm();
    }
    if (mode === DIALOG_MODE_EDIT) {
      // setChecked(false);
      form.resetForm();
    }
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
      // setChecked(false);
    }
  };

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };
  const handleOpenSaveAsTemplateDialog = async (value: boolean) => {
    setOpenSaveAsTemplateDialog(value);
  };
  const handleSaveAsTemplate = (value: boolean) => {
    setSaveAsTemplate(value);
    form.handleSubmit();
  };

  const handleDataReturn = (name: string) => {
    setNameSaveAsTemplate(name);
  };
  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='xl'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('estimate.createEstimate') : t('estimate.editEstimate')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent className='p-0'>
        <Box className={cx('create-estimate-container')}>
          {/* <Box className='sticky top-0 z-50 bg-white'> */}
          <ClientInformation data={props.data} />

          <Box className='w-full p-5'>
            <InputLabel>{t('property.name', { ns: 'admin/invoice' })}</InputLabel>
            <TextField
              name='name'
              value={form.values?.name}
              onChange={form.handleChange}
              error={Boolean(form.errors?.name)}
              helperText={form.errors?.name}
            />
          </Box>
          <PaymentCalculation
            form={form}
            subtotal={cast(form.values.subtotal)}
            totalTax={companyTax}
            editData={editData}
            onChangeTax={setCompanyTax}
          />
          {/* </Box> */}
          <Box>
            <OpeningStatement form={form} editData={editData} />
          </Box>
          <Box>
            <PriceListItem form={form} editData={editData} companyTax={companyTax} />
          </Box>
          <Box>
            <FileItem jobId={props.jobId} form={form} editData={editData} typeData={FILE_ITEM_FOR_ESTIMATE} />
          </Box>
          <Box>
            <TermAndCondition form={form} editData={editData} />
          </Box>
          <Box>
            <Esign editData={editData} mode={mode} form={form} customerEmail={props.data?.customer?.email} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          color='secondary'
          variant='contained'
          onClick={() => {
            setOpenSaveAsTemplateDialog(true);
          }}
        >
          {t('saveAsNewTemplate', { ns: 'admin/common' })}
        </Button>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          disabled={disableSubmit}
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>

      <SaveAsTemplateDialog
        open={openSaveAsTemplateDialog}
        form={form}
        setOpen={handleOpenSaveAsTemplateDialog}
        setSaveAsTemplate={handleSaveAsTemplate}
        handleDataReturn={handleDataReturn}
      />
    </Dialog>
  );
}

export default memo(CreateEstimateDialog);
